// src/features/rrhh/mensajeria/components/MensajeForm.jsx
import { Box, Button, Dialog, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import { useMemo, useEffect, useState } from "react";
import RichTextEditor from "../../contenido/components/RichTextEditor";

function FieldPill({ children }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1,
        py: 0.75,
        borderRadius: 999,
        bgcolor: (t) => t.palette?.secondary?.[200],
        boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.40)",
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: (t) => t.palette?.primary?.[900], lineHeight: 1 }}>
        {children}
      </Typography>
    </Box>
  );
}

function InlineFieldRow({ label, children, sx }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        width: "100%",
        minWidth: 0,
        ...sx,
      }}
    >
      <FieldPill>{label}</FieldPill>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Stack>
  );
}

export default function MensajeForm({
  open,
  onClose,
  temas = [],
  alcances = [],
  sucursales = [],
  loadingSucursales = false,
  variant = "dialog",
  onSubmit,
}) {
  const temaOptions = useMemo(
    () => (Array.isArray(temas) && temas.length ? temas : ["Otro"]),
    [temas]
  );
  const alcanceOptions = useMemo(
    () => (Array.isArray(alcances) && alcances.length ? alcances : ["Empresa"]),
    [alcances]
  );

  const [tema, setTema] = useState(() => temaOptions[0] ?? "Otro");
  const [alcance, setAlcance] = useState(() => alcanceOptions[0] ?? "Empresa");
  const [asunto, setAsunto] = useState("");
  const [contenido, setContenido] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onSubmit?.({ tema, alcance, asunto, contenido, idSucursal });
      onClose?.();
    } finally {
      setSubmitting(false);
    }
  };

  const [idSucursal, setIdSucursal] = useState("");

  // si cambia a Empresa, limpio sucursal
  useEffect(() => {
    if (alcance !== "Sucursal") setIdSucursal("");
  }, [alcance]);

  // ✅ Inline (parecido Figma)
  if (variant === "inline") {
    return (
      <Box sx={{ width: "100%" }}>
        <Stack spacing={1}>
          {/* Tema + Alcance (Alcance a la derecha, mismo input que te gustó) */}
          <Stack direction="row" spacing={2}>
            <Box sx={{ flex: 1 }}>
              <InlineFieldRow label="Tema">
                <TextField
                  select
                  value={tema}
                  onChange={(e) => setTema(e.target.value)}
                  SelectProps={{ native: true }}
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: 32,
                      borderRadius: 2,
                      bgcolor: (t) => t.palette?.neutral?.[50],
                      boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.45)",
                      "& fieldset": { border: "none" },
                    },
                  }}
                >
                  {temaOptions.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </TextField>
              </InlineFieldRow>
            </Box>

            <Box sx={{ flex: 1 }}>
              <InlineFieldRow label="Alcance">
                <TextField
                  select
                  value={alcance}
                  onChange={(e) => setAlcance(e.target.value)}
                  SelectProps={{ native: true }}
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: 32,
                      borderRadius: 2,
                      bgcolor: (t) => t.palette?.neutral?.[50],
                      boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.45)",
                      "& fieldset": { border: "none" },
                    },
                  }}
                >
                  {alcanceOptions.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </TextField>
              </InlineFieldRow>
              {alcance === "Sucursal" && (
                <Box sx={{ mt: 1 }}>
                  <FieldPill>Sucursal</FieldPill>
                  <Box sx={{ mt: 0.5 }}>
                    <TextField
                      select
                      value={idSucursal}
                      onChange={(e) => setIdSucursal(e.target.value)}
                      SelectProps={{ native: true }}
                      fullWidth
                      size="small"
                      disabled={loadingSucursales || !sucursales.length}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: 32,
                          borderRadius: 2,
                          bgcolor: (t) => t.palette?.neutral?.[50],
                          boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.45)",
                          "& fieldset": { border: "none" },
                        },
                      }}
                    >
                      <option value="">
                        {loadingSucursales ? "Cargando..." : "Seleccioná una sucursal"}
                      </option>
                      {sucursales.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name || s.nombre || s.nombre_sucursal || `Sucursal #${s.id}`}
                        </option>
                      ))}
                    </TextField>
                  </Box>
                </Box>
              )}
            </Box>
          </Stack>

          {/* Título */}
          <InlineFieldRow label="Título">
            <TextField
              //label="Ingresa un titulo corto"
              value={asunto}
              onChange={(e) => setAsunto(e.target.value)}
              placeholder="Ingresa un título corto"
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 32,
                  borderRadius: 2,
                  bgcolor: (t) => t.palette?.neutral?.[50],
                  boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.45)",
                  "& fieldset": { border: "none" },
                },
              }}
            />
          </InlineFieldRow>

          {/* Contenido */}
          <Box>
            <FieldPill>Contenido</FieldPill>
            <Box sx={{ mt: 0.5 }}>
              <TextField
                value={contenido}
                onChange={(e) => setContenido(e.target.value)}
                placeholder="máximo 800 caracteres"
                fullWidth
                multiline
                minRows={4} // (no te cambio tamaños)
                inputProps={{ maxLength: 800 }}
                helperText={`${contenido.length}/800`}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    minHeight: 137,
                    borderRadius: 2,
                    bgcolor: (t) => t.palette?.neutral?.[50],
                    boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.45)",
                    "& fieldset": { border: "none" },
                  },
                }}
              />
            </Box>
          </Box>

          {/* CTA */}
          <Box sx={{ display: "flex", justifyContent: "center", pt: 0 }}>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !asunto.trim() || !contenido.trim()}
              sx={{
                px: 2,
                py: 1,
                borderRadius: 999,
                bgcolor: (t) => t.palette?.primary?.[500],
                color: "common.white",
                "&:hover": { bgcolor: (t) => t.palette?.primary?.[600] },
              }}
            >
              {submitting ? "Enviando…" : "Enviar Mensaje"}
            </Button>
          </Box>
        </Stack>
      </Box>
    );
  }

  // Dialog
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Box
            sx={{
              px: 3,
              py: 1,
              borderRadius: 999,
              bgcolor: (t) => t.palette?.secondary?.[200],
              boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.45)",
            }}
          >
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: (t) => t.palette?.primary?.[900] }}>
              Enviar nuevo mensaje
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField select label="Tema" value={tema} onChange={(e) => setTema(e.target.value)} SelectProps={{ native: true }} fullWidth>
              {temaOptions.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </TextField>

            <TextField select label="Alcance" value={alcance} onChange={(e) => setAlcance(e.target.value)} SelectProps={{ native: true }} fullWidth>
              {alcanceOptions.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </TextField>
          </Stack>

          <TextField
            label="Asunto"
            value={asunto}
            onChange={(e) => setAsunto(e.target.value)}
            placeholder="Título del mensaje"
            fullWidth
          />
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "primary.900", mb: 1 }}>Contenido</Typography>
            <RichTextEditor
              value={contenido}
              onChange={setContenido}
              placeholder="Escribe el contenido del mensaje..."
              minHeight={200}
              readOnly={false}
            />
          </Box>

          <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end", pt: 1 }}>
            <Button
              onClick={onClose}
              sx={{
                borderRadius: 999,
                bgcolor: (t) => t.palette?.neutral?.[150],
                "&:hover": { bgcolor: (t) => t.palette?.neutral?.[200] },
              }}
            >
              Cancelar
            </Button>

            <Button
              onClick={handleSubmit}
              disabled={submitting || !asunto.trim() || !contenido.trim()}
              sx={{
                borderRadius: 999,
                bgcolor: (t) => t.palette?.secondary?.[500],
                color: "common.white",
                "&:hover": { bgcolor: (t) => t.palette?.secondary?.[600] },
              }}
            >
              {submitting ? "Enviando..." : "Enviar"}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
